import { useIntl } from "react-intl";
import React, { useState } from "react";

// Mui
import {
    Box,
    Typography,
    TableRow,
    TableCell,
    IconButton,
    Tooltip,
    Collapse,
    Popover,
    ClickAwayListener,
    Theme,
} from "@mui/material";

// Icons
import icons from "enums/icons";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

// Utils
import { format } from "date-fns";

// Styles
import UserTableRowStyles from "./UserTableRow.styles";

// Components
import CustomIcon from "components/CustomIcon";
import { InfoWithIcon } from "pages/ClientHub/components";
import { TeamChips } from "../TeamChips";
import { SelectTeam } from "../SelectTeam";
import PersonWorkHistory from "components/PersonWorkHistory";

// Types
import { Team } from "services/user_management/user_management.types";
import { TPersonInfo } from "components/Team/Team.types";
import { TConvertedUser } from "pages/UserManagement/adaptares/converter";

const initialPersonInfo = {
    role: "",
    id: -1,
    info: undefined,
};

const UserTableRow = ({
    data,
    teams,
    Checkbox,
    RoleSelectInput,
    visibleColumns,
    hidableColumns,
    handleOpenArchiveModal,
    onChipSelect,
    setSelectedUser,
    handleConfirmAddTeam,
    removeTeam,
}: {
    data: TConvertedUser;
    teams: Team[];
    Checkbox: React.ReactNode;
    RoleSelectInput: React.ReactNode;
    visibleColumns: any[];
    hidableColumns: any[];
    handleOpenArchiveModal: (event: React.MouseEvent<HTMLElement, MouseEvent>, userData: any) => void;
    onChipSelect: (value: string) => void;
    setSelectedUser: (userId: string) => void;
    handleConfirmAddTeam: (selectedUserIds: number[], newTeams: string[]) => Promise<void>;
    removeTeam: (user_id: number, teamName: string) => Promise<void>;
}) => {
    const intl = useIntl();
    const classes = UserTableRowStyles();
    const [shouldExpand, setShouldExpand] = useState(false);
    const hasLinkedin = Boolean(data.person.linkedin_id);

    const [shouldOpenTeamsModal, setShouldOpenTeamsModal] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const [toggleShowWorkHistory, setToggleShowWorkHistory] = useState(false);
    const [WHAnchorEl, setWHAnchorEl] = useState<HTMLElement | null>(null);
    const [personInfo, setPersonInfo] = useState<TPersonInfo>(initialPersonInfo);

    const handleToggleExpand = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        handleStopPropagation(event);
        setShouldExpand(!shouldExpand);
    };

    const handleStopPropagation = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
    };

    const handleOpenTeamsModal = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, userId: string) => {
        handleStopPropagation(event);
        setAnchorEl(event.currentTarget);
        setShouldOpenTeamsModal(true);
        setSelectedUser(userId);
    };

    const handleCloseTeamsModal = () => {
        setShouldOpenTeamsModal(false);
        setAnchorEl(null);
    };

    const handleLinkedinClick = (ev: React.SyntheticEvent) => {
        ev.stopPropagation();
    };

    const onClickRestoreIcon = (event: React.MouseEvent<HTMLElement>) => {
        if (!data?.person.id) return;

        setToggleShowWorkHistory(true);
        setPersonInfo({
            role: data.job_title || "",
            id: data.person.id,
        });
        setWHAnchorEl(event.currentTarget);
    };

    const onCloseWH = () => {
        setToggleShowWorkHistory(false);
        setWHAnchorEl(null);
    };

    return (
        <>
            <TableRow onClick={(event) => handleToggleExpand(event)}>
                <TableCell>{Checkbox}</TableCell>
                <TableCell>
                    <InfoWithIcon
                        startSlot={
                            <Box display="flex" alignItems="center">
                                <IconButton onClick={(event) => handleToggleExpand(event)} sx={{ pl: 0 }}>
                                    <CustomIcon
                                        icon={icons.info}
                                        sx={(theme: Theme) => ({ color: theme.palette.info.main, fontSize: 14 })}
                                    />
                                </IconButton>
                                {!data.optin_share_data && (
                                    <CustomIcon icon={icons.paceIconNotShared} sx={{ fontSize: 14, ml: 0, mr: 0.5 }} />
                                )}
                            </Box>
                        }
                        entityName={data.name}
                    />
                </TableCell>
                <TableCell data-hidden={visibleColumns.includes("role")}>{RoleSelectInput}</TableCell>
                <TableCell data-hidden={visibleColumns.includes("jobTitle")}>{data.job_title}</TableCell>
                <TableCell data-hidden={visibleColumns.includes("last_active_datetime")} align="center">
                    {format(new Date(data.last_active_datetime), "dd/MM/yyyy")}
                </TableCell>
                <TableCell data-hidden={visibleColumns.includes("team")} sx={{ display: "flex", alignItems: "center" }}>
                    <TeamChips
                        chips={data.teams}
                        onChipSelect={onChipSelect}
                        data={data}
                        handleOpenTeamsModal={(ev) => handleOpenTeamsModal(ev, data.id.toString())}
                        removeTeam={removeTeam}
                    />
                </TableCell>
                <TableCell align="center">
                    <IconButton
                        className={classes.removeUserButton}
                        size="small"
                        onClick={(event) => handleOpenArchiveModal(event, data)}
                    >
                        <Tooltip
                            arrow
                            placement="bottom-start"
                            title={intl.formatMessage({
                                id: "userManagement.message.removeUserTooltip",
                            })}
                        >
                            <DeleteOutlinedIcon />
                        </Tooltip>
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow className={classes.expandableRow}>
                <TableCell colSpan={hidableColumns.length + 3}>
                    <Collapse in={shouldExpand} timeout="auto" unmountOnExit>
                        <Box display="flex" alignItems="center" py={1.2} px={0}>
                            <Box display="flex" alignItems="center" mr={2}>
                                <IconButton
                                    size="small"
                                    sx={{ color: "primary.dark", p: 0, mr: 1 }}
                                    onClick={onClickRestoreIcon}
                                    disabled={!data?.person.id}
                                >
                                    <RestoreIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{ color: "primary.dark", p: 0 }}
                                    aria-label="linkedin button"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disabled={!hasLinkedin}
                                    href={hasLinkedin ? `https://www.linkedin.com/in/${data.person.linkedin_id}/` : "#"}
                                    onClick={handleLinkedinClick}
                                >
                                    <CustomIcon icon={icons.linkedin} sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Box>
                            {data.email && (
                                <Box display="flex" alignItems="center" mr={2} color="text.secondary">
                                    <Typography mr={1}>
                                        {intl.formatMessage({
                                            id: "hubManagement.client.userTable.email",
                                        })}
                                        :
                                    </Typography>
                                    <Typography>{data.email}</Typography>
                                </Box>
                            )}
                            {hasLinkedin && (
                                <Box display="flex" alignItems="center" color="text.secondary">
                                    <Typography mr={1}>
                                        {intl.formatMessage({
                                            id: "hubManagement.client.userTable.linkedInID",
                                        })}
                                        :
                                    </Typography>
                                    <Typography>{data.person.linkedin_id}</Typography>
                                </Box>
                            )}

                            <IconButton
                                className={classes.toggleCloseBtn}
                                onClick={(event) => handleToggleExpand(event)}
                            >
                                <CustomIcon icon={icons.close} />
                            </IconButton>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
            <Popover
                open={shouldOpenTeamsModal}
                onClose={handleCloseTeamsModal}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                sx={{
                    "& .MuiPaper-root": {
                        borderRadius: "30px",
                        border: "1px solid #102F52",
                    },
                }}
            >
                <SelectTeam
                    list={teams}
                    users={data}
                    cancel={handleCloseTeamsModal}
                    confirm={(selectedUserIds, newTeams) => handleConfirmAddTeam(selectedUserIds, newTeams)}
                    isMultipleUsers={false}
                />
            </Popover>

            {toggleShowWorkHistory ? (
                <>
                    <ClickAwayListener onClickAway={onCloseWH}>
                        <PersonWorkHistory
                            role={""}
                            personId={personInfo.id}
                            anchorEl={WHAnchorEl}
                            onClose={onCloseWH}
                            open={toggleShowWorkHistory}
                        />
                    </ClickAwayListener>
                </>
            ) : null}
        </>
    );
};

export default UserTableRow;
